import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { Box, Button, Stack, Typography } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined';
import ShuffleIcon from '@mui/icons-material/Shuffle';
import PageLayout from '../components/global/PageLayout';
import PageLoading from '../components/global/PageLoading';
import SingleSelectFilter from '../components/filtering/SingleSelectFilter';
import FlashbookDesignsTab from '../components/flashbook/FlashbookDesignsTab';
import FlashDesignSidebarModal from '../components/flashbook/FlashDesignSidebarModal';
import FlashbookSettingsFormModal from '../components/flashbook/FlashbookSettingsFormModal';
import {
  getShopFlashDesignUrl,
  userHasAccessToShopFlash,
} from '../helpers/flashbooks';
import useUser from '../hooks/global/useUser';
import useBusiness from '../hooks/businesses/useBusiness';
import useConfirm from '../hooks/useConfirm';
import useGetBusinessFlashbook from '../hooks/flashbooks/useGetBusinessFlashbook';
import useGetFlashbookDesigns from '../hooks/flashbooks/useGetFlashbookDesigns';
import useUpdateFlashbookDesign from '../hooks/flashbooks/useUpdateFlashbookDesign';
import useUpdateDesignsSortOrder from '../hooks/flashbooks/useUpdateDesignsSortOrder';
import { ROUTES } from '../constants/global';
import { FooterAction } from '../interfaces';
import { FlashDesignWithRelations } from '../types/flashbooks';

type FilterValue = 'active' | 'archived';

export default function ShopFlashbookPage() {
  const { user } = useUser();
  const navigate = useNavigate();
  const { business } = useBusiness();
  const confirm = useConfirm();
  const { data: flashbook, isLoading: isLoadingFlashbook } =
    useGetBusinessFlashbook();
  const { mutateAsync: updateDesign } = useUpdateFlashbookDesign();
  const { mutateAsync: updateSortOrder, isPending: isSavingOrder } =
    useUpdateDesignsSortOrder();
  const { designs: activeDesigns } = useGetFlashbookDesigns({
    flashbookId: flashbook?.id,
    isArchived: false,
  });

  const [selectedDesign, setSelectedDesign] =
    useState<FlashDesignWithRelations | null>(null);
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);
  const [isReorderMode, setIsReorderMode] = useState(false);
  const [reorderedDesigns, setReorderedDesigns] = useState<
    FlashDesignWithRelations[]
  >([]);

  const onStartReorder = () => {
    setReorderedDesigns(activeDesigns);
    setIsReorderMode(true);
  };

  const onCancelReorder = () => {
    setIsReorderMode(false);
    setReorderedDesigns([]);
  };

  const onReorderChange = (next: FlashDesignWithRelations[]) => {
    setReorderedDesigns(next);
  };

  const onSaveReorder = async () => {
    if (!flashbook?.id || !reorderedDesigns.length) return;
    const sortedDesignIds = reorderedDesigns.map(d => d.id);
    try {
      await updateSortOrder({
        flashbookId: flashbook.id,
        payload: { sortedDesignIds },
      });
      toast.success('Storefront order saved');
      setIsReorderMode(false);
      setReorderedDesigns([]);
    } catch {
      toast.error('Failed to save order');
    }
  };

  const onAdd = () => navigate(`${ROUTES.flashbook}/new`);
  const onSelectDesign = (design: FlashDesignWithRelations) =>
    setSelectedDesign(design);
  const onCloseSidebar = () => setSelectedDesign(null);

  const navigateToEdit = (designId: number) =>
    navigate(`${ROUTES.flashbook}/${designId}/edit`);

  const copyDesignLink = async (design: FlashDesignWithRelations) => {
    const url = getShopFlashDesignUrl(business, design.externalId);
    if (!url) {
      toast.error('Storefront URL is not configured');
      return;
    }
    try {
      await navigator.clipboard.writeText(url);
      toast.success('Link copied');
    } catch {
      toast.error('Failed to copy link');
    }
  };

  const onEdit = () => {
    if (!selectedDesign) return;
    navigateToEdit(selectedDesign.id);
  };

  const onPreview = () => {
    if (!selectedDesign) return;
    const url = getShopFlashDesignUrl(business, selectedDesign.externalId);
    if (!url) {
      toast.error('Storefront URL is not configured');
      return;
    }
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  const onCopyLink = async () => {
    if (!selectedDesign) return;
    await copyDesignLink(selectedDesign);
  };

  const onArchive = async () => {
    if (!selectedDesign || !flashbook?.id) return;
    const isArchived = !!selectedDesign.archivedAt;
    const accepted = await confirm({
      title: isArchived ? 'Restore design?' : 'Archive design?',
      message: isArchived
        ? 'Restoring will make this design active again.'
        : 'Archiving will hide this design from your storefront.',
      positiveText: isArchived ? 'Restore' : 'Archive',
    });
    if (!accepted) return;
    try {
      await updateDesign({
        flashbookId: flashbook.id,
        flashDesignId: selectedDesign.id,
        payload: { archived: !isArchived },
      });
      toast.success(isArchived ? 'Design restored' : 'Design archived');
      setSelectedDesign(null);
    } catch {
      toast.error('Failed to update design');
    }
  };

  const sidebarFooterActions: FooterAction[] | undefined = selectedDesign
    ? [
        { label: 'Copy link', action: onCopyLink },
        { label: 'Edit Flash', action: onEdit },
      ]
    : undefined;

  const sidebarMoreActions: FooterAction[] | undefined = selectedDesign
    ? [
        { label: 'Edit Flash', action: onEdit },
        { label: 'Preview', action: onPreview },
        {
          label: selectedDesign.archivedAt ? 'Restore' : 'Archive',
          action: onArchive,
        },
      ]
    : undefined;

  const [searchParams, setSearchParams] = useSearchParams();
  const filterParam = searchParams.get('tab') as FilterValue | null;
  const filter: FilterValue =
    filterParam === 'archived' ? 'archived' : 'active';

  const setFilter = (next: FilterValue) => {
    const params = new URLSearchParams(searchParams);
    if (next === 'active') {
      params.delete('tab');
    } else {
      params.set('tab', next);
    }
    setSearchParams(params, { replace: true });
  };

  const isOnActiveTab = filter === 'active';
  const canReorder =
    !isReorderMode && isOnActiveTab && activeDesigns.length > 1;

  return (
    <PageLayout
      headerIsHidden
      guard={{
        isAllowed: !!user && userHasAccessToShopFlash(user),
      }}>
      {isLoadingFlashbook ? (
        <PageLoading />
      ) : (
        <>
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            gap={2}
            mb={1.5}>
            <Typography variant="h1">Shop Flash</Typography>
            <Stack direction="row" gap={1.5} alignItems="center">
              <Button
                variant="light"
                startIcon={<SettingsOutlinedIcon />}
                onClick={() => setIsSettingsOpen(true)}>
                Settings
              </Button>
              <Button variant="base" startIcon={<AddIcon />} onClick={onAdd}>
                Add New Flash
              </Button>
            </Stack>
          </Stack>

          {isReorderMode ? (
            <Stack
              direction="row"
              gap={1.5}
              alignItems="center"
              justifyContent="space-between"
              sx={{
                position: 'sticky',
                top: 56,
                zIndex: 20,
                mx: -4,
                mb: 2.5,
                px: 4,
                py: 1.5,
                backgroundColor: 'lightGrey.main',
                borderTop: '1px solid',
                borderBottom: '1px solid',
                borderColor: 'darkerLightGrey.main',
              }}>
              <Stack direction="row" gap={1.25} alignItems="center">
                <ShuffleIcon
                  fontSize="small"
                  sx={{ color: 'text.secondary' }}
                />
                <Box sx={{ typography: 'h5' }}>
                  Drag any flash to change the order it appears on your
                  storefront.
                </Box>
              </Stack>
              <Stack direction="row" gap={1}>
                <Button
                  variant="light"
                  onClick={onCancelReorder}
                  disabled={isSavingOrder}
                  sx={{ height: 34, typography: 'h5', fontWeight: 700 }}>
                  Cancel
                </Button>
                <Button
                  variant="base"
                  onClick={onSaveReorder}
                  disabled={isSavingOrder}
                  sx={{ height: 34, typography: 'h5', fontWeight: 700 }}>
                  {isSavingOrder ? 'Saving…' : 'Save order'}
                </Button>
              </Stack>
            </Stack>
          ) : (
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
              mb={2.5}>
              <SingleSelectFilter
                options={[
                  { label: 'Active Designs', value: 'active' },
                  { label: 'Archived Designs', value: 'archived' },
                ]}
                state={filter}
                setState={(next: React.SetStateAction<string>) => {
                  const value =
                    typeof next === 'function' ? next(filter) : next;
                  setFilter(value as FilterValue);
                }}
                desktopWidth={220}
              />
              {canReorder ? (
                <Button
                  variant="light"
                  startIcon={<ShuffleIcon sx={{ fontSize: 14 }} />}
                  onClick={onStartReorder}
                  sx={{
                    height: 36,
                    px: 1.75,
                    borderRadius: 1,
                    textTransform: 'none',
                    typography: 'h5',
                  }}>
                  Re-order
                </Button>
              ) : null}
            </Stack>
          )}

          <FlashbookDesignsTab
            flashbookId={flashbook?.id}
            isArchived={!isOnActiveTab}
            onAdd={onAdd}
            onOpenSettings={() => setIsSettingsOpen(true)}
            onSelectDesign={onSelectDesign}
            onEditDesign={d => navigateToEdit(d.id)}
            onCopyLinkDesign={copyDesignLink}
            isReorderMode={isReorderMode && isOnActiveTab}
            onReorderChange={onReorderChange}
            reorderedDesigns={reorderedDesigns}
          />
        </>
      )}
      <FlashDesignSidebarModal
        flashbookId={flashbook?.id}
        flashDesignId={selectedDesign?.id}
        isOpen={selectedDesign !== null}
        onClose={onCloseSidebar}
        footerActions={sidebarFooterActions}
        moreActions={sidebarMoreActions}
      />
      <FlashbookSettingsFormModal
        open={isSettingsOpen}
        onClose={() => setIsSettingsOpen(false)}
        flashbookId={flashbook?.id}
        initialValues={
          flashbook
            ? {
                enabled: flashbook.enabled,
                enableDirectBookings: flashbook.enableDirectBookings,
                depositBehavior: flashbook.depositBehavior,
                depositAmount: String(flashbook.depositAmount ?? ''),
                depositPercentage: String(
                  (flashbook.depositPercentage ?? 0) * 100,
                ),
              }
            : undefined
        }
      />
    </PageLayout>
  );
}
